<template>
  <FullCalendar :options="calendarOptions" />
</template>

<script setup lang="ts">
import type { CalendarOptions } from '@fullcalendar/core/index.js'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/vue3'
// import { formatIntlDate } from '~/utils/dateFormat'

export interface HmlMyCampaignCalendarStatisticsModel {
  date: string
  type: 'part' | 'all' | 'none'
  isVolunteer: boolean
}

const props = defineProps<{
  stats: HmlMyCampaignCalendarStatisticsModel[]
}>()

const emits = defineEmits<{
  (event: 'dateClick', info: { date: Date }): void
  (event: 'onMonthChange', info: { yearMonth: string }): void
}>()

const calendarOptions = ref<CalendarOptions>({})

const { $t } = useNuxtApp()
const { locale } = useI18n()
const { transData } = useTranslation()

const capitalizeFirstLetterByLocale = (text: string, localeCode: string) => {
  if (!text) {
    return text
  }

  const firstChar = text.charAt(0)
  if (firstChar === firstChar.toLocaleUpperCase(localeCode)) {
    return text
  }

  return `${firstChar.toLocaleUpperCase(localeCode)}${text.slice(1)}`
}

watch(
  // 통계 데이터 변경 시 캘린더 옵션 업데이트
  // 언어 변경 시 다국어 데이터가 불러오기 전에 dayHeaderContent 함수가 호출되는 문제가 있어서 transData.value를 추가함
  () => [props.stats, transData.value],
  () => {
    calendarOptions.value = {
      plugins: [dayGridPlugin, interactionPlugin],

      dateClick: (info) => {
        emits('dateClick', { date: info.date })
      },
      headerToolbar: {
        start: 'title',
      },
      firstDay: 0,
      dayHeaderContent: (args) => {
        const days = ['일', '월', '화', '수', '목', '금', '토']
        return $t(days[args.date.getUTCDay()] ?? '')
      },

      datesSet(event) {
        const midDate = new Date((event.start.getTime() + event.end.getTime()) / 2)
        const yearMonth = midDate.toISOString().slice(0, 7)
        emits('onMonthChange', { yearMonth })
      },
      titleFormat: (args) => {
        // IOS에서 날짜 포맷이 잘못되는 문제가 있어서 수정 (ex : 2024-12-1 이면 invalid format 에러 발생 2024-12-01 이렇게 포맷 변경)
        // const date = new Date(
        //   `${args.date.year}-${args.date.month + 1 < 10 ? `0${args.date.month + 1}` : args.date.month + 1}-${
        //     args.date.day < 10 ? `0${args.date.day}` : args.date.day
        //   }`,
        // )
        const nowDate = new Date(`${args.date.year}-${args.date.month + 1 < 10 ? `0${args.date.month + 1}` : args.date.month + 1}`)
        const titleLocale = args.localeCodes[0] ?? 'en-US'
        const title = new Intl.DateTimeFormat([titleLocale, 'en-US'], { year: 'numeric', month: 'long' }).format(nowDate)
        return capitalizeFirstLetterByLocale(title, titleLocale)
      },
      locale: locale.value,
      dayCellContent(e) {
        return e.date.getDate()
      },

      dayCellClassNames(info) {
        const dateString = formatYYYYmmdd(info.date)
        const data = props.stats.find(stat => stat.date === dateString)
        const classes = []
        if (data?.type === 'part') {
          classes.push('engagement-part')
        }
        else if (data?.type === 'all') {
          classes.push('engagement-all')
        }

        return classes
      },
      buttonText: {
        today: $t('today'),
      },
    }
  },
  {
    deep: true,
    immediate: true,
  },
)
</script>
